import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Toolbar,
  IconButton,
  Container,
  Badge,
  CardMedia,
  CardActions,
  Button,
  Collapse,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

export default function CustomerDevice() {

  const navigate = useNavigate();

  const [showcaseDevices, setShowcaseDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchShowcaseDevices();
  }, []);

  const backward = () => {
    navigate('/customerHome');
  };

  const handleLogout = () => {
    // Remove user details from session storage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    console.log('User details cleared from session storage');
    navigate('/');
  };

  const fetchShowcaseDevices = async () => {
    try {
      const response = await axios.get('https://app.smartco.live/showcasedevice/getShowcaseDevice');
      setShowcaseDevices(response.data);
    } catch (error) {
      console.error('Error fetching showcase devices:', error);
    }
  };

  const handleOpenDetails = (device) => {
    setSelectedDevice(device);
    setOpenDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDialog(false);
    setSelectedDevice(null);
  };

  return (
    <Container maxWidth="lg">
      <Toolbar
        sx={{
          pr: '24px',
          marginTop: '20px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <IconButton color="inherit" sx={{ marginRight: 'auto' }} onClick={backward}>
            <Box
              sx={{
                background: 'linear-gradient(90deg, rgba(198, 61, 231, 0.2), rgba(117, 40, 136, 0.2))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
              }}
            >
              <ArrowBackIcon />
            </Box>
          </IconButton>

          <Typography
            component="h1"
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              background: 'linear-gradient(90deg, #C63DE7, #752888)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: 'Public Sans, sans-serif',
              fontWeight: 'bold'
            }}
          >
            SMARTCO
          </Typography>

          <IconButton onClick={handleLogout} sx={{ marginLeft: 'auto' }}>
            <Box
              sx={{
                background: 'linear-gradient(90deg, rgba(198, 61, 231, 0.2), rgba(117, 40, 136, 0.2))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
              }}
            >
              <LogoutIcon />
            </Box>
          </IconButton>
        </Box>
      </Toolbar>

      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            mb: 2,
            fontFamily: 'Public Sans, sans-serif',
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #C63DE7, #752888)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Our Devices
        </Typography>
        <Typography
          variant="body1"
          align="center"
          sx={{
            mb: 4,
            color: '#637381',
            fontFamily: 'Public Sans, sans-serif',
          }}
        >
          If you want to buy any device contact us
          <br />
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              mt: 1,
              color: '#752888',
              fontWeight: 'bold'
            }}
          >
            <PhoneIcon sx={{ mr: 0.5, fontSize: 20 }} />
            69966882
          </Box>
        </Typography>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {showcaseDevices.map((device, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(117, 40, 136, 0.2)',
                  }
                }}
                onClick={() => handleOpenDetails(device)}
              >
                <CardMedia
                  component="img"
                  image={device.imageName || '/placeholder-image.png'}
                  alt={device.deviceName}
                  sx={{
                    width: '100%',
                    height: { xs: 250, sm: 200 },
                    objectFit: 'contain',
                    backgroundColor: '#f5f5f5',
                    padding: 1
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{
                      fontFamily: 'Public Sans, sans-serif',
                      fontWeight: 'bold',
                      color: '#212B36',
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {device.deviceName}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {device.storage && (
                      <Chip
                        label={device.storage}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(117, 40, 136, 0.1)',
                          color: '#752888',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                    {device.ram && (
                      <Chip
                        label={device.ram}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(198, 61, 231, 0.1)',
                          color: '#C63DE7',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                    {device.color && (
                      <Chip
                        label={device.color}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(99, 115, 129, 0.1)',
                          color: '#637381',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'Public Sans, sans-serif',
                      fontWeight: 'bold',
                      background: 'linear-gradient(90deg, #C63DE7, #752888)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {device.price} KWD
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#752888',
                      '&:hover': {
                        backgroundColor: '#C63DE7',
                      },
                      fontFamily: 'Public Sans, sans-serif',
                      fontWeight: 'bold',
                      borderRadius: 2,
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {showcaseDevices.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: '#637381'
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
              No devices available at the moment
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please check back later or contact us for more information
            </Typography>
          </Box>
        )}
      </Box>

      {/* Device Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: { xs: 2, sm: 3 }
          }
        }}
      >
        {selectedDevice && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
                fontFamily: 'Public Sans, sans-serif',
                fontWeight: 'bold'
              }}
            >
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontFamily: 'Public Sans, sans-serif',
                  fontWeight: 'bold',
                  background: 'linear-gradient(90deg, #C63DE7, #752888)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {selectedDevice.deviceName}
              </Typography>
              <IconButton onClick={handleCloseDetails} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <CardMedia
                    component="img"
                    image={selectedDevice.imageName || '/placeholder-image.png'}
                    alt={selectedDevice.deviceName}
                    sx={{
                      borderRadius: 2,
                      width: '100%',
                      height: 'auto',
                      maxHeight: { xs: 250, md: 300 },
                      objectFit: 'contain',
                      backgroundColor: '#f5f5f5',
                      padding: 2
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: 'Public Sans, sans-serif',
                        fontWeight: 'bold',
                        background: 'linear-gradient(90deg, #C63DE7, #752888)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2
                      }}
                    >
                      {selectedDevice.price} KWD
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Model Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.modelNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Color
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.color}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Storage
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.storage}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        RAM
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.ram}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Shop Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.shopName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Warranty
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}>
                        {selectedDevice.warrenty}
                      </Typography>
                    </Grid>
                  </Grid>

                  {selectedDevice.description && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Public Sans, sans-serif' }}>
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: '#637381',
                          fontFamily: 'Public Sans, sans-serif',
                          lineHeight: 1.6
                        }}
                      >
                        {selectedDevice.description}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PhoneIcon />}
                sx={{
                  backgroundColor: '#752888',
                  '&:hover': {
                    backgroundColor: '#C63DE7',
                  },
                  fontFamily: 'Public Sans, sans-serif',
                  fontWeight: 'bold',
                  py: 1.5,
                  fontSize: '1rem'
                }}
                href="tel:69966882"
              >
                Call Us: 69966882
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
}

